/**
 * Fire-and-forget telemetry client. All calls swallow errors — telemetry
 * must never break the wallet flow.
 *
 * Three events ship:
 *
 *   device_probe   — once per device, on the first wallet creation
 *                    + on every cold start (so we catch storage-quota
 *                    or memory changes over time)
 *   proof_timing   — every successful Send / Swap. Lets us see the
 *                    real-world distribution of proof times by device
 *                    class.
 *   proof_oom      — every failed send where the worker reported a
 *                    memory error. Counts toward "should we ship
 *                    chunked proving?"
 *
 * No personally identifiable information is sent. The bot side
 * de-anonymises only by storing `tg_user_id` (already required for
 * initData validation), and we deliberately don't echo it back in
 * any UI.
 */

import { type DeviceAssessment } from "./device-probe";

const BOT_BASE_URL =
  (import.meta.env.VITE_TELESWAP_BOT_URL as string | undefined) ?? "";

function getInitData(): string {
  const w = (typeof window !== "undefined"
    ? (window as Window & {
        Telegram?: { WebApp?: { initData?: string } };
      })
    : null);
  return w?.Telegram?.WebApp?.initData ?? "";
}

async function postEvent(event: Record<string, unknown>): Promise<void> {
  if (!BOT_BASE_URL) return;
  try {
    await fetch(`${BOT_BASE_URL.replace(/\/+$/, "")}/api/telecrypt/telemetry`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Telegram-Init-Data": getInitData(),
      },
      body: JSON.stringify(event),
      keepalive: true,
    });
  } catch {
    // best-effort
  }
}

export function reportDeviceProbe(a: DeviceAssessment): void {
  void postEvent({
    event: "device_probe",
    device_class: a.class,
    reason: a.reason,
    wasm_simd: a.probe.wasmSimd,
    shared_array_buffer: a.probe.sharedArrayBuffer,
    device_memory_gb: a.probe.deviceMemoryGb,
    js_heap_limit_mb: a.probe.jsHeapLimitMb,
    storage_quota_mb: a.probe.storageQuotaMb,
    storage_available_mb: a.probe.storageAvailableMb,
    user_agent: a.probe.userAgent,
  });
}

export function reportProofTiming(args: {
  kind: "send" | "swap";
  device_class: "weak" | "ok" | "strong";
  total_ms: number;
  proving_ms: number | null;
  decompress_ms: number | null;
  spend_count: number;
  output_count: number;
}): void {
  void postEvent({
    event: "proof_timing",
    ...args,
  });
}

export function reportProofOOM(args: {
  kind: "send" | "swap";
  device_class: "weak" | "ok" | "strong";
  message: string;
}): void {
  void postEvent({
    event: "proof_oom",
    ...args,
  });
}
